import Button from '@/components/UI/Button';
import Loading from '@/components/Loading';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { renderMetadataValue, successToast } from '@/helpers';
import { _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { MetadataField, MetadataPostItem } from '@/model/Metadata.model';
import type { BulkSamplesFormData, SampleData, SampleMeasurement, SampleMeasurementKey } from '@/model/Sample.model';
import type { Study } from '@/model/Study.model';
import { type FC, lazy, Suspense, useRef, useState } from 'react';
import { useRequest } from '@/support/Hooks/request';
import { formatColumnSettingsDataValue } from '@/support/hot';
import { formatColumnSettingsDataValueV16 } from '@/support/hot-v16';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { BrowserRouter, Link } from 'react-router-dom';
import { web } from '@/support/route.ts';
import { RouteService } from '@/support/RouteService';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';

import styles from './ViewBulkSampleDetails.module.scss';

const ViewBulkSampleDetailsTable = lazy(() => import('./ViewBulkSampleDetailsTable'));
const ViewBulkSampleDetailsTableV16 = lazy(() => import('./ViewBulkSampleDetailsTableV16'));

interface ViewBulkSampleDetailsProps {
  studyId: Study['id'];
  subjects: Array<Animal>;
  closeModal: () => void;
  handleCallback: (finished: boolean, viewSamples: boolean) => void;
  sampleDetails: BulkSamplesFormData;
  metadata?: Array<MetadataField>;
}
const featureSelector = createSelector([featuresSelector], (features) => ({
  plannedSamplesEnabled: features?.planned_samples ?? false,
  hotUpgrade: features?.hot_upgrade ?? false,
}));
const ViewBulkSampleDetails: FC<ViewBulkSampleDetailsProps> = ({
  studyId,
  subjects,
  sampleDetails,
  metadata = [],
  closeModal,
  handleCallback,
}) => {
  const { plannedSamplesEnabled, hotUpgrade } = useSelector(featureSelector);

  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  // TODO: remove once v12 is fully replaced by v16 and lazy loading is no longer needed
  const [tableReady, setTableReady] = useState<boolean>(false);
  const dataRef = useRef<Array<SampleData>>([]);

  const {
    sendRequest: createSamples,
    requestSending: creatingSamples,
    requestError,
  } = useRequest({
    route: 'samples.bulk.create',
    method: 'post',
    params: { studyId },
  });

  const formatEncoder = hotUpgrade ? formatColumnSettingsDataValueV16 : formatColumnSettingsDataValue;

  const formatMetadata = (meta: Record<string, string>) =>
    Object.entries(meta).reduce<Array<MetadataPostItem>>((acc, [key, value]) => {
      const glossaryDetails = metadata.find(({ title }) => formatEncoder(title) === key);
      let formattedValue: string | string[];
      if (_notNil(glossaryDetails) && _isNotEmpty(value)) {
        if (glossaryDetails.field_type === 'date') {
          formattedValue = value;
        } else if (glossaryDetails.field_type === 'multi_select') {
          formattedValue = (value as string).split(',');
        } else {
          formattedValue = renderMetadataValue({
            value,
            field_type: glossaryDetails.field_type,
          }).toString();
        }

        acc.push({
          glossary_id: glossaryDetails.id,
          value: formattedValue,
        });
      }
      return acc;
    }, []);

  const resolveTimepoint = (timepoint: unknown): string | undefined => {
    if (Array.isArray(timepoint)) {
      return _isNotEmpty(timepoint) ? String(timepoint[0]) : undefined;
    }
    if (typeof timepoint === 'string' && _isNotBlank(timepoint)) {
      return timepoint;
    }
    return undefined;
  };

  const mapFormDataForPost = () =>
    dataRef.current.map(({ subject_id, subjectName, cageName, date, comments, meta, details, timepoint }) => {
      const formattedMeta = formatMetadata(meta ?? {});
      const resolvedTimepoint = resolveTimepoint(timepoint);
      return {
        subject_id,
        date,
        comments,
        team_glossary_api_id: sampleDetails.sample_type.api_id,
        details: Object.entries(details).reduce<Array<SampleMeasurement>>((acc, [key, value]) => {
          if (_notNil(value) && _isNotEmpty(value)) {
            acc.push({
              key: key as SampleMeasurementKey,
              value,
              unit: sampleDetails?.details?.[key as SampleMeasurementKey]?.unit ?? ('' as SampleMeasurement['unit']),
            });
          }
          return acc;
        }, []),
        ...(_notNil(resolvedTimepoint) ? { timepoint: resolvedTimepoint } : {}),
        ...(_isNotEmpty(formattedMeta) ? { metadata: formattedMeta } : {}),
      };
    });

  const handleSave = async (finished: boolean) => {
    await createSamples({ samples: mapFormDataForPost() });
    if (requestError === false) {
      successToast(
        <div>
          Samples collected from {dataRef.current.length} animal{Number(dataRef.current.length) !== 1 ? 's. ' : '. '}
          <BrowserRouter>
            <Link
              to={web('studies.samples', { id: studyId })}
              className="link"
              onClick={() => handleCallback(finished, true)}
            >
              View Samples
            </Link>
          </BrowserRouter>
        </div>
      );
    }
    handleCallback(finished, false);
  };

  return (
    <ModalContainer size="wide" className="bg-white">
      <div className="flex flex-column pa3">
        <ModalHeader title="Samples details" closeModal={closeModal} className="w-100" />
        <p className="lh-copy f6">
          Review and amend samples and their associated data.
          <a
            href={RouteService.help('bulk_sample_collection')}
            target="_blank"
            rel="noopener noreferrer"
            className="link blue ml1"
          >
            Read more
          </a>
        </p>
      </div>
      <div data-testid="sample-details-container" className={`ow-spreadsheet-styles mr0 ${styles.container}`}>
        <Suspense fallback={<Loading />}>
          {hotUpgrade ? (
            <ViewBulkSampleDetailsTableV16
              subjects={subjects}
              metadata={metadata}
              setInvalid={setIsInvalid}
              sampleDetails={sampleDetails}
              plannedSamplesEnabled={plannedSamplesEnabled}
              dataRef={dataRef}
              onReady={setTableReady}
            />
          ) : (
            <ViewBulkSampleDetailsTable
              subjects={subjects}
              metadata={metadata}
              setInvalid={setIsInvalid}
              sampleDetails={sampleDetails}
              plannedSamplesEnabled={plannedSamplesEnabled}
              dataRef={dataRef}
              onReady={setTableReady}
            />
          )}
        </Suspense>
      </div>
      <div className="flex g3 items-center w-100 pa3">
        <ModalActions
          submitBtnText="Save and finish"
          submitButtonProps={{
            className: 'ml3',
            disabled: creatingSamples || isInvalid || !tableReady,
            tooltip: isInvalid ? spreadSheetValidationTooltip : undefined,
          }}
          onSubmit={() => handleSave(true)}
        />
        <Button
          disabled={creatingSamples || isInvalid || !tableReady}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
          soft
          onClick={() => handleSave(false)}
          testKey="bulk-sample-save-button"
        >
          Save and collect another
        </Button>
      </div>
    </ModalContainer>
  );
};

export default ViewBulkSampleDetails;
